/**
 * Application state and the draft poller (FR-5, FR-11, FR-12, NFR-2 – NFR-4).
 *
 * A small observable store rather than a framework. Everything the UI renders
 * is derived from `AppState`, and every mutation goes through `update()` so a
 * single re-render path can keep the board stable under the cursor (FR-39).
 *
 * The engine is recomputed only when its inputs actually change, not on every
 * poll: a poll that returns no new picks must not reshuffle the board or cost
 * a full valuation pass on a phone.
 */

import type {
  AdpData,
  AdpRow,
  DraftPick,
  DraftState,
  LeagueConfig,
  Player,
  PlayerAvailabilityDetail,
  PlayerId,
  PlayerUniverse,
  RankingSource,
} from '../core/types.ts';
import { classifyPlayerAvailability, isExcludedFromCurrentSeason } from '../core/injuries.ts';
import { PlayerIndex } from '../core/matching.ts';
import { blendSources, defaultBlendWeights, positionRanks, type BlendResult } from '../core/blend.ts';
import { projectFromCurves } from '../core/projections.ts';
import { buildPool, orderByValue } from '../core/valuation.ts';
import { runEngine, type EngineOutput } from '../core/recommend.ts';
import { orderByDynasty, valueDynastyPool, type DynastyValuation } from '../core/dynasty.ts';
import { runBlueprint, type BlueprintOutput, type BlueprintSnapshot } from '../core/blueprint.ts';
import { indexAdp } from '../core/availability.ts';
import { applySpecialistBoardPolicy, specialistPolicyFor } from '../core/specialistPolicy.ts';
import { deriveLeagueConfig } from '../core/scoring.ts';
import {
  buildDraftContext,
  getDraft,
  getLeague,
  getLeagueUsers,
  getPicks,
  getRosters,
  toDraftState,
  type DraftContext,
} from '../sources/sleeper.ts';
import { loadAllSources, type SourceReport } from '../sources/registry.ts';
import { loadBlueprintSnapshot, type BlueprintLoadStatus } from '../sources/blueprint.ts';
import { loadPlayerAvailabilityFeed } from '../sources/injuries.ts';
import { loadUserSources, saveUserSources } from './sourceCache.ts';
import {
  loadDraftOverrides,
  saveDraftOverrides,
  type StoredDraftOverride,
} from './storage.ts';
import type { AppConfig } from './config.ts';
import { saveConfig } from './config.ts';

export type ConnectionStatus = 'live' | 'stale' | 'offline' | 'idle';

export interface StorePersistence {
  loadSources: () => Promise<RankingSource[]>;
  saveSources: (sources: RankingSource[]) => void | Promise<unknown>;
  saveConfig: (config: AppConfig) => void | Promise<unknown>;
  loadAvailabilityOverrides: (draftId: string) => StoredDraftOverride[];
  saveAvailabilityOverrides: (draftId: string, overrides: StoredDraftOverride[]) => void | Promise<unknown>;
}

export interface StoreOptions {
  /** A shared setup link explicitly chose the initial sorting source. */
  explicitActiveSource?: boolean;
}

export const GUEST_PERSISTENCE: StorePersistence = {
  loadSources: loadUserSources,
  saveSources: saveUserSources,
  saveConfig,
  loadAvailabilityOverrides: loadDraftOverrides,
  saveAvailabilityOverrides: saveDraftOverrides,
};

export interface AvailabilityOverride extends StoredDraftOverride {
  player_id: PlayerId;
}

export interface AppState {
  phase: 'onboarding' | 'loading' | 'board' | 'error';
  config: AppConfig;
  universe: PlayerUniverse | null;
  playerAvailability: Map<PlayerId, PlayerAvailabilityDetail>;
  index: PlayerIndex | null;
  league: LeagueConfig | null;
  leagueOverridden: string[];
  draftContext: DraftContext | null;
  draft: DraftState | null;
  sources: RankingSource[];
  sourceReports: SourceReport[];
  adp: AdpData | null;
  adpIndex: Map<PlayerId, AdpRow>;
  blend: BlendResult | null;
  /**
   * Every loaded source weighted equally, regardless of which one is sorting
   * the board (FR-13, FR-14).
   *
   * `blend` above is scoped to the *active* source when a single source is
   * selected, because that is what drives the board order. Reusing it for the
   * per-player comparison would mean the side-by-side view listed exactly one
   * source and the disagreement score was always zero — the two things the
   * multi-source design exists to surface.
   */
  comparison: BlendResult | null;
  engine: EngineOutput | null;
  /** Research-based forecast output, only populated when Blueprint is active. */
  blueprint: BlueprintOutput | null;
  /** Last validated open-data forecast artifact, even when another mode sorts the board. */
  blueprintSnapshot: BlueprintSnapshot | null;
  blueprintStatus: BlueprintLoadStatus;
  blueprintMessage: string | null;
  /**
   * Dynasty value per available player (§12). Always computed, not only when
   * the board is sorted by it — the player panel shows it beside the redraft
   * number so a keeper-league decision can weigh both.
   */
  dynastyValues: Map<PlayerId, DynastyValuation>;
  /** FR-12: picks the user entered by hand. */
  manualPicks: DraftPick[];
  /** Local corrections to synced picks; never sent to Supabase. */
  availabilityOverrides: Map<PlayerId, AvailabilityOverride>;
  connection: ConnectionStatus;
  lastPollAt: number | null;
  lastPollError: string | null;
  loadingMessage: string;
  error: string | null;
  /** Non-fatal notices for the footer (staleness, degraded sources). */
  notices: string[];
}

export type Listener = (state: AppState) => void;

export class Store {
  private state: AppState;
  private listeners = new Set<Listener>();
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollAbort: AbortController | null = null;
  private pollGeneration = 0;
  private persistence: StorePersistence = GUEST_PERSISTENCE;
  /** Cache key for the last engine run, so polls that change nothing are free. */
  private lastEngineKey = '';
  private readonly explicitActiveSource: boolean;

  constructor(config: AppConfig, options: StoreOptions = {}) {
    this.explicitActiveSource = options.explicitActiveSource ?? false;
    this.state = {
      phase: 'onboarding',
      config,
      universe: null,
      playerAvailability: new Map(),
      index: null,
      league: null,
      leagueOverridden: [],
      draftContext: null,
      draft: null,
      sources: [],
      sourceReports: [],
      adp: null,
      adpIndex: new Map(),
      blend: null,
      comparison: null,
      engine: null,
      blueprint: null,
      blueprintSnapshot: null,
      blueprintStatus: 'disabled',
      blueprintMessage: null,
      dynastyValues: new Map(),
      manualPicks: [],
      availabilityOverrides: new Map(),
      connection: 'idle',
      lastPollAt: null,
      lastPollError: null,
      loadingMessage: '',
      error: null,
      notices: [],
    };
  }

  get(): AppState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  update(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }

  patchConfig(patch: Partial<AppConfig>): void {
    const config = { ...this.state.config, ...patch };
    void this.persistence.saveConfig(config);
    this.update({ config });
  }

  setPersistence(persistence: StorePersistence): void {
    this.persistence = persistence;
  }

  replaceConfig(config: AppConfig): void {
    void this.persistence.saveConfig(config);
    this.update({ config });
  }

  replaceSources(sources: RankingSource[]): void {
    this.update({ sources });
    void this.persistence.saveSources(sources);
    this.recompute(true);
  }

  resetToOnboarding(config: AppConfig): void {
    this.stopPolling();
    this.lastEngineKey = '';
    this.update({
      phase: 'onboarding',
      config,
      universe: null,
      index: null,
      league: null,
      leagueOverridden: [],
      draftContext: null,
      draft: null,
      sources: [],
      sourceReports: [],
      adp: null,
      adpIndex: new Map(),
      blend: null,
      comparison: null,
      engine: null,
      blueprint: null,
      blueprintSnapshot: null,
      blueprintStatus: 'disabled',
      blueprintMessage: null,
      dynastyValues: new Map(),
      manualPicks: [],
      availabilityOverrides: new Map(),
      connection: 'idle',
      lastPollAt: null,
      lastPollError: null,
      loadingMessage: '',
      error: null,
      notices: [],
    });
  }

  // -------------------------------------------------------------------------
  // Setup
  // -------------------------------------------------------------------------

  /**
   * Resolve league, draft, and every ranking source, then run the engine once.
   *
   * All of it browser-direct except `/api/adp` (FR-63): no league ID, username,
   * roster, or draft state is transmitted to a Pages Function.
   */
  async initialiseDraft(
    universe: PlayerUniverse,
    leagueId: string,
    draftId: string,
    myUserId: string | null,
  ): Promise<void> {
    this.update({ phase: 'loading', loadingMessage: 'Reading league settings…', error: null });

    try {
      const index = new PlayerIndex(universe, aliasMap(this.state.config.aliases));

      const [league, draft, users, rosters] = await Promise.all([
        getLeague(leagueId),
        getDraft(draftId),
        getLeagueUsers(leagueId),
        getRosters(leagueId),
      ]);

      const { config: leagueConfig, overridden } = deriveLeagueConfig(league, draft, {
        overrides: this.state.config.league_overrides as Partial<LeagueConfig>,
      });

      const injuryFeed = await loadPlayerAvailabilityFeed(String(leagueConfig.season));
      const playerAvailability = new Map<PlayerId, PlayerAvailabilityDetail>();
      for (const [playerId, player] of universe) {
        playerAvailability.set(
          playerId,
          classifyPlayerAvailability(player, injuryFeed.feed?.players.get(playerId)),
        );
      }

      const draftContext = buildDraftContext(draft, users, rosters, myUserId);
      leagueConfig.draft_slot = draftContext.myDraftSlot;

      this.update({ loadingMessage: 'Loading ranking sources…' });

      // Rung 0 of the §4.6 ladder: whatever the user pasted in a previous
      // session, restored from IndexedDB so it doesn't have to be re-pasted
      // every time the tab reopens (player ids are stable across sessions,
      // so the rows need no re-matching).
      const userSources = await this.persistence.loadSources();

      const loaded = await loadAllSources({
        universe,
        index,
        league: leagueConfig,
        userSources,
      });

      const blendWeights = Object.keys(this.state.config.blend_weights).length
        ? this.state.config.blend_weights
        : defaultBlendWeights(loaded.sources);

      const picks = await getPicks(draftId);
      const draftState = toDraftState(draftContext, picks, this.state.manualPicks, leagueConfig.teams);
      const availabilityOverrides = reconcileAvailabilityOverrides(
        this.persistence.loadAvailabilityOverrides(draftId),
        draftState,
      );

      this.update({
        universe,
        playerAvailability,
        index,
        league: leagueConfig,
        leagueOverridden: overridden,
        draftContext,
        draft: draftState,
        availabilityOverrides,
        sources: loaded.sources,
        sourceReports: loaded.reports,
        adp: loaded.adp,
        adpIndex: indexAdp(loaded.adp),
        notices: [
          ...loaded.reports.filter((r) => r.status !== 'ok' && r.message).map((r) => r.message!),
          ...(injuryFeed.message ? [`Injuries: ${injuryFeed.message}`] : []),
        ],
        connection: 'live',
        lastPollAt: Date.now(),
      });
      this.persistAvailabilityOverrides(availabilityOverrides);

      // Resolve the league-appropriate default before showing the board. A
      // Dynasty startup or rookie draft asks a multi-year question; redraft
      // and keeper drafts use the research-based Blueprint mode. A source in
      // a shared link is an explicit choice and wins over both defaults.
      const defaultSource = leagueConfig.league_type === 'dynasty' ? 'dynasty' : 'blueprint';
      const activeSourceId = this.explicitActiveSource
        ? this.state.config.active_source_id
        : defaultSource;

      this.patchConfig({
        league_id: leagueId,
        draft_id: draftId,
        blend_weights: blendWeights,
        active_source_id: activeSourceId,
      });

      // Redraft/keeper boards wait for the default Blueprint decision so the
      // first visible board is not briefly rendered in a different mode. A
      // Dynasty board can open immediately while Blueprint loads for its mode
      // switch; the result never changes its selected default.
      if (activeSourceId === 'blueprint') {
        const blueprintReady = await this.loadBlueprint();
        if (!blueprintReady) this.patchConfig({ active_source_id: 'blend' });
      } else {
        void this.loadBlueprint();
      }

      this.update({ phase: 'board' });
      this.recompute(true);
      this.startPolling();
    } catch (err) {
      this.update({
        phase: 'error',
        error: err instanceof Error ? err.message : 'Could not load the draft.',
      });
    }
  }

  // -------------------------------------------------------------------------
  // Engine
  // -------------------------------------------------------------------------

  /**
   * Load the published, versioned Blueprint artifact. Redraft and keeper
   * startup waits for this result because Blueprint is their default; Dynasty
   * startup can continue while the mode loads in the background. A missing or
   * expired artifact disables the mode and never silently substitutes the
   * rank-based engine.
   */
  private async loadBlueprint(): Promise<boolean> {
    const result = await loadBlueprintSnapshot();
    const notices = this.state.notices.filter((notice) => !notice.startsWith('Blueprint:'));
    if (result.message) notices.push(`Blueprint: ${result.message}`);

    if (result.status === 'ready' && result.snapshot) {
      this.update({
        blueprintSnapshot: result.snapshot,
        blueprintStatus: 'ready',
        blueprintMessage: result.message,
        notices,
      });
      if (this.state.config.active_source_id === 'blueprint') this.recompute(true);
      return true;
    }

    const activeWasBlueprint = this.state.config.active_source_id === 'blueprint';
    if (activeWasBlueprint) {
      this.patchConfig({ active_source_id: 'blend' });
      notices.push('Blueprint is disabled until a valid forecast snapshot is published.');
    }
    this.update({
      blueprint: null,
      blueprintSnapshot: null,
      blueprintStatus: 'disabled',
      blueprintMessage: result.message,
      notices,
    });
    if (activeWasBlueprint) this.recompute(true);
    return false;
  }

  /**
   * Recompute the blend, projections, and recommendations.
   *
   * Skipped when nothing relevant changed (`force` bypasses). The key covers
   * everything the output depends on — pick count, roster, weights, active
   * source, loaded sources — so a 5-second poll on an idle draft costs one
   * string comparison rather than a valuation pass over 700 players.
   */
  recompute(force = false): void {
    const { universe, index, league, draft, sources, config, playerAvailability } = this.state;
    if (!universe || !index || !league || !draft) return;

    const key = [
      draftFingerprint(draft),
      availabilityOverrideFingerprint(this.state.availabilityOverrides),
      config.active_source_id,
      config.engine.baseline_method,
      JSON.stringify(config.engine.weights),
      JSON.stringify(config.blend_weights),
      sources.map((s) => `${s.source_id}:${s.rows.length}`).join(','),
      this.state.blueprintStatus,
      this.state.blueprintSnapshot?.model_version ?? 'no-blueprint',
      [...playerAvailability.entries()]
        .filter(([, availability]) => availability.status === 'season_out')
        .map(([id]) => id)
        .join(','),
    ].join('|');

    if (!force && key === this.lastEngineKey) return;
    this.lastEngineKey = key;

    // The active source drives the board sort (FR-16). "blend" mixes every
    // loaded source by weight; "value" also blends (it still needs a rank
    // order to derive positional ranks and curve projections from, below) but
    // then re-sorts the board by VOR instead of blended percentile — see below.
    // Anything else isolates one source by giving it all the weight, which
    // keeps a single code path for all three cases.
    const weights =
      config.active_source_id === 'blend' ||
      config.active_source_id === 'value' ||
      config.active_source_id === 'dynasty' ||
      config.active_source_id === 'blueprint'
        ? config.blend_weights
        : { [config.active_source_id]: 1 };

    const blend = blendSources(sources, weights);

    // FR-13/FR-14: the per-player comparison must show every source the user
    // loaded, not just whichever one happens to be sorting the board. Equal
    // weights, because this view is a comparison rather than a ranking — it is
    // never used to order anything.
    const comparison =
      sources.length > 1
        ? blendSources(sources, Object.fromEntries(sources.map((s) => [s.source_id, 1])))
        : blend;

    const blueprintRankSources = sources.filter(
      (source) => (source.kind === 'rank' || source.kind === 'user') && source.source_id !== 'sleeper-search-rank',
    );
    const blueprintRankView = blueprintRankSources.length
      ? blendSources(
          blueprintRankSources,
          Object.fromEntries(blueprintRankSources.map((source) => [source.source_id, 1])),
        ).byPlayer
      : null;

    // `all_rosters` is a superset of `draft.picks`: it also carries players a
    // dynasty team already owns from a prior season, who are just as
    // undraftable as a pick made earlier this event (see `toDraftState`).
    const effectiveDraft = applyAvailabilityOverrides(draft, this.state.availabilityOverrides);
    const drafted = new Set(Object.values(effectiveDraft.all_rosters).flat());
    const seasonOut = new Set(
      league.league_type === 'dynasty'
        ? []
        : [...playerAvailability.entries()]
          .filter(([, availability]) => isExcludedFromCurrentSeason(availability, league.league_type))
          .map(([id]) => id),
    );
    const availableIds = blend.order.filter((id) => !drafted.has(id) && !seasonOut.has(id));

    const positionOf = (id: PlayerId) => universe.get(id)?.position ?? null;
    const posRanks = positionRanks(availableIds, positionOf);

    // Position ranks are computed over the *available* pool rather than the
    // full list on purpose: once the top four tight ends are gone, the fifth is
    // the best one left, and his projection should reflect that. Ranking him
    // against a static preseason list would price a mid-draft TE as if the
    // cliff had not already happened.
    const projections = projectFromCurves(
      availableIds
        .map((id) => {
          const player = universe.get(id);
          const rank = posRanks.get(id);
          if (!player?.position || !rank) return null;
          return { player_key: id, position: player.position, position_rank: rank };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null),
      league,
    );

    const availablePlayers = availableIds
      .map((id) => universe.get(id))
      .filter((p): p is Player => p !== undefined);

    const pool = buildPool(
      availablePlayers,
      (id) => posRanks.get(id) ?? null,
      (id) => blend.byPlayer.get(id)?.blended_rank ?? null,
      projections,
    );

    const sourceTiers = new Map<PlayerId, number | null>();
    for (const source of sources) {
      if (source.source_id !== config.active_source_id) continue;
      for (const row of source.rows) {
        if (row.player_key && row.tier !== null) sourceTiers.set(row.player_key, row.tier);
      }
    }

    const engine = runEngine({
      league,
      draft: effectiveDraft,
      config: config.engine,
      available: pool,
      rankView: blend.byPlayer,
      rankedCount: blend.order.length,
      adp: this.state.adpIndex,
      projections,
      playerOf: (id) => universe.get(id),
      tiers: sourceTiers.size ? sourceTiers : undefined,
    });

    // Dynasty value is computed whenever the model can say something, not only
    // when the board is sorted by it: the player panel shows a dynasty number
    // alongside the redraft one so the two can be compared, which is most of
    // the point in a keeper league.
    const dynastyValues = valueDynastyPool(
      pool.map((entry) => ({
        player_key: entry.player.player_id_sleeper,
        position: entry.position,
        age: entry.player.age,
        proj_points: entry.proj_points,
      })),
      league,
      // Measured above replacement, using the engine's own baselines, for the
      // same reason the redraft "Value" board is: raw points make every
      // quarterback look like the best asset on the board.
      { replacement: engine.baselines.points },
    );

    let board = blend;
    const excludedInOrder = blend.order.filter((id) => seasonOut.has(id) && !drafted.has(id));
    if (config.active_source_id === 'value') {
      const draftedInOrder = blend.order.filter((id) => drafted.has(id));
      board = { ...blend, order: orderByValue(availableIds, [...draftedInOrder, ...excludedInOrder], engine.valuations) };
    } else if (config.active_source_id === 'dynasty') {
      const draftedInOrder = blend.order.filter((id) => drafted.has(id));
      board = { ...blend, order: orderByDynasty(availableIds, [...draftedInOrder, ...excludedInOrder], dynastyValues) };
    }

    let blueprint: BlueprintOutput | null = null;
    if (config.active_source_id === 'blueprint' && this.state.blueprintStatus === 'ready' && this.state.blueprintSnapshot) {
      blueprint = runBlueprint({
        league,
        draft: effectiveDraft,
        snapshot: this.state.blueprintSnapshot,
        available: pool,
        rankView: blueprintRankView,
        adp: this.state.adpIndex,
        playerOf: (id) => universe.get(id),
      });
      const draftedInOrder = blend.order.filter((id) => drafted.has(id));
      board = {
        ...blend,
        order: [
          ...blueprint.order,
          ...draftedInOrder.filter((id) => !blueprint!.valuations.has(id)),
          ...excludedInOrder,
        ],
      };
    }

    if (league.league_type === 'redraft') {
      const specialistPolicy = specialistPolicyFor(effectiveDraft, league, engine.need);
      board = {
        ...board,
        order: applySpecialistBoardPolicy(
          board.order,
          new Set(availableIds),
          (id) => id,
          (id) => positionOf(id),
          specialistPolicy,
        ),
      };
    }

    this.update({ blend: board, comparison, engine, dynastyValues, blueprint });
  }

  // -------------------------------------------------------------------------
  // Polling (FR-5, FR-11)
  // -------------------------------------------------------------------------

  startPolling(options: { immediate?: boolean } = {}): void {
    this.stopPolling();
    const generation = this.pollGeneration;
    const tick = async () => {
      if (generation !== this.pollGeneration || this.state.phase !== 'board') return;
      await this.pollOnce();
      if (generation !== this.pollGeneration || this.state.phase !== 'board') return;
      const interval = Math.max(this.state.config.engine.poll_interval_ms, 1000);
      this.pollTimer = setTimeout(tick, interval);
    };
    if (options.immediate) void tick();
    else {
      const interval = Math.max(this.state.config.engine.poll_interval_ms, 1000);
      this.pollTimer = setTimeout(tick, interval);
    }
  }

  stopPolling(): void {
    this.pollGeneration += 1;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    this.pollAbort?.abort();
    this.pollAbort = null;
  }

  /**
   * One poll cycle.
   *
   * FR-11: transient errors are retried with backoff by the HTTP layer, the
   * last good state stays on screen, and the connection indicator goes stale
   * rather than the board going blank. NFR-3: no exception from here may
   * propagate — a thrown error during a draft is the one failure mode that
   * cannot be recovered from in the moment.
   */
  async pollOnce(): Promise<void> {
    const { draftContext, league } = this.state;
    if (!draftContext || !league) return;

    this.pollAbort?.abort();
    const controller = new AbortController();
    this.pollAbort = controller;

    try {
      const picks = await getPicks(draftContext.draft.draft_id, controller.signal);
      if (controller.signal.aborted || this.pollAbort !== controller || this.state.draftContext !== draftContext) return;
      const draft = toDraftState(draftContext, picks, this.state.manualPicks, league.teams);
      const availabilityOverrides = reconcileAvailabilityOverrides(
        [...this.state.availabilityOverrides.values()],
        draft,
      );
      this.update({
        draft,
        availabilityOverrides,
        connection: 'live',
        lastPollAt: Date.now(),
        lastPollError: null,
      });
      this.persistAvailabilityOverrides(availabilityOverrides);
      this.recompute();
    } catch (err) {
      if (controller.signal.aborted || this.pollAbort !== controller) return;
      const age = this.state.lastPollAt ? Date.now() - this.state.lastPollAt : Infinity;
      this.update({
        connection: age > 30_000 ? 'offline' : 'stale',
        lastPollError: err instanceof Error ? err.message : 'Poll failed.',
      });
    }
  }

  // -------------------------------------------------------------------------
  // Manual pick entry (FR-12)
  // -------------------------------------------------------------------------

  /**
   * Mark a player drafted by hand.
   *
   * This is the escape hatch that keeps the tool useful when Sleeper sync
   * breaks or the draft is happening off-platform, so it goes through exactly
   * the same state path as a synced pick — the board, the engine, and the
   * roster panel cannot tell the difference.
   */
  markDrafted(playerId: PlayerId, slot?: number): void {
    const { draft, league, draftContext } = this.state;
    if (!draft || !league || !draftContext) return;
    const override = this.state.availabilityOverrides.get(playerId);
    if (override) {
      const availabilityOverrides = new Map(this.state.availabilityOverrides);
      availabilityOverrides.delete(playerId);
      this.persistAvailabilityOverrides(availabilityOverrides);
      this.update({ availabilityOverrides });
      this.recompute(true);
      return;
    }
    if (draft.picks.some((p) => p.player_id === playerId)) return;

    const pick_no = draft.current_pick_no;
    const round = Math.ceil(pick_no / league.teams);
    const draft_slot = slot ?? slotForPick(pick_no, league.teams, league.draft_type);

    const roster_id = draftContext.draft.slot_to_roster_id?.[String(draft_slot)] ?? null;
    const manual: DraftPick = {
      pick_no,
      round,
      draft_slot,
      roster_id,
      player_id: playerId,
      picked_by: null,
      team_name: roster_id !== null ? draftContext.rosterNames.get(roster_id) ?? `Roster ${roster_id}` : `Slot ${draft_slot}`,
      manual: true,
    };

    const manualPicks = [...this.state.manualPicks, manual];
    this.update({
      manualPicks,
      draft: toDraftState(draftContext, syncedOnly(draft), manualPicks, league.teams),
    });
    this.recompute(true);
  }

  markUndrafted(playerId: PlayerId): void {
    const { draft, league, draftContext } = this.state;
    if (!draft || !league || !draftContext) return;
    const manualPicks = this.state.manualPicks.filter((p) => p.player_id !== playerId);
    const syncedPick = draft.picks.find((pick) => pick.player_id === playerId && !pick.manual);
    const availabilityOverrides = new Map(this.state.availabilityOverrides);
    if (syncedPick) {
      availabilityOverrides.set(playerId, {
        player_id: playerId,
        pick_no: syncedPick.pick_no,
        draft_slot: syncedPick.draft_slot,
        roster_id: syncedPick.roster_id,
      });
    }
    this.update({
      manualPicks,
      availabilityOverrides,
      draft: toDraftState(draftContext, syncedOnly(draft), manualPicks, league.teams),
    });
    this.persistAvailabilityOverrides(availabilityOverrides);
    this.recompute(true);
  }

  private persistAvailabilityOverrides(overrides: Map<PlayerId, AvailabilityOverride>): void {
    const draftId = this.state.draft?.draft_id;
    if (!draftId) return;
    void this.persistence.saveAvailabilityOverrides(draftId, [...overrides.values()]);
  }

  // -------------------------------------------------------------------------
  // Sources
  // -------------------------------------------------------------------------

  addSource(source: RankingSource): void {
    const sources = [...this.state.sources.filter((s) => s.source_id !== source.source_id), source];
    const blend_weights = { ...this.state.config.blend_weights };
    if (blend_weights[source.source_id] === undefined) blend_weights[source.source_id] = 0.6;

    this.update({ sources });
    this.patchConfig({ blend_weights, active_source_id: source.source_id });
    this.recompute(true);
    // Fire-and-forget: losing this write just means a re-paste next session,
    // never something worth blocking the board on.
    void this.persistence.saveSources(sources);
  }

  removeSource(sourceId: string): void {
    const sources = this.state.sources.filter((s) => s.source_id !== sourceId);
    const active =
      this.state.config.active_source_id === sourceId
        ? sources[0]?.source_id ?? 'sleeper-search-rank'
        : this.state.config.active_source_id;
    this.update({ sources });
    this.patchConfig({ active_source_id: active });
    this.recompute(true);
    void this.persistence.saveSources(sources);
  }

  setActiveSource(sourceId: string): void {
    if (sourceId === 'blueprint' && this.state.blueprintStatus !== 'ready') {
      this.update({
        blueprintMessage: this.state.blueprintMessage ?? 'Blueprint is disabled until a valid forecast snapshot is published.',
        notices: [...this.state.notices.filter((notice) => !notice.startsWith('Blueprint:')), 'Blueprint: forecast snapshot unavailable.'],
      });
      return;
    }
    this.patchConfig({ active_source_id: sourceId });
    this.recompute(true);
  }

  /** FR-49: record a manual name resolution so the correction persists. */
  addAlias(normalizedName: string, playerId: PlayerId): void {
    const aliases = { ...this.state.config.aliases, [normalizedName]: playerId };
    this.patchConfig({ aliases });
    if (this.state.universe) {
      this.update({ index: new PlayerIndex(this.state.universe, aliasMap(aliases)) });
    }
  }
}

function aliasMap(aliases: Record<string, string>): Map<string, PlayerId> {
  return new Map(Object.entries(aliases));
}

function draftFingerprint(draft: DraftState): string {
  const picks = draft.picks
    .map((pick) => `${pick.pick_no}:${pick.player_id}:${pick.draft_slot}:${pick.roster_id ?? ''}:${pick.manual ? 'm' : 's'}`)
    .join(',');
  const rosters = Object.entries(draft.all_rosters)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([rosterId, players]) => `${rosterId}:${players.join(',')}`)
    .join('|');
  return `${picks}|${rosters}|${draft.current_pick_no}|${draft.my_draft_slot ?? ''}`;
}

function availabilityOverrideFingerprint(overrides: Map<PlayerId, AvailabilityOverride>): string {
  return [...overrides.values()]
    .sort((a, b) => a.player_id.localeCompare(b.player_id))
    .map((override) => `${override.player_id}:${override.pick_no}:${override.draft_slot}:${override.roster_id ?? ''}`)
    .join(',');
}

function reconcileAvailabilityOverrides(
  stored: StoredDraftOverride[],
  draft: DraftState,
): Map<PlayerId, AvailabilityOverride> {
  const picks = new Map(draft.picks.filter((pick) => !pick.manual).map((pick) => [pick.player_id, pick]));
  const active = new Map<PlayerId, AvailabilityOverride>();
  for (const override of stored) {
    const pick = picks.get(override.player_id);
    if (
      pick &&
      pick.pick_no === override.pick_no &&
      pick.draft_slot === override.draft_slot &&
      pick.roster_id === override.roster_id
    ) {
      active.set(override.player_id, { ...override });
    }
  }
  return active;
}

function applyAvailabilityOverrides(
  draft: DraftState,
  overrides: Map<PlayerId, AvailabilityOverride>,
): DraftState {
  if (!overrides.size) return draft;
  const overridden = new Set(overrides.keys());
  const all_rosters = Object.fromEntries(
    Object.entries(draft.all_rosters).map(([rosterId, players]) => [
      rosterId,
      players.filter((playerId) => !overridden.has(playerId)),
    ]),
  );
  return {
    ...draft,
    picks: draft.picks.filter((pick) => !overridden.has(pick.player_id)),
    my_roster: draft.my_roster.filter((playerId) => !overridden.has(playerId)),
    all_rosters,
  };
}

/** Strip manual entries so they can be re-merged cleanly. */
function syncedOnly(draft: DraftState): Parameters<typeof toDraftState>[1] {
  return draft.picks
    .filter((p) => !p.manual)
    .map((p) => ({
      pick_no: p.pick_no,
      round: p.round,
      draft_slot: p.draft_slot,
      roster_id: p.roster_id,
      player_id: p.player_id,
      picked_by: p.picked_by ?? '',
    }));
}

function slotForPick(pickNo: number, teams: number, type: string): number {
  const round = Math.ceil(pickNo / teams);
  const indexInRound = ((pickNo - 1) % teams) + 1;
  if (type === 'linear') return indexInRound;
  return round % 2 === 0 ? teams - indexInRound + 1 : indexInRound;
}
